import { useEffect, useState } from "react";
import {
  useGetProductsQuery,
  useGetWarehousesQuery,
  useAdjustStockMutation,
} from "../../../../redux/api";
import type { StockAdjustmentReason } from "../../../../redux/api";
import { Modal } from "../../../../components/Modal";
import { Field } from "../../../../components/Field";

interface StockAdjustmentFormModalProps {
  open: boolean;
  onClose: () => void;
  prefillProductId?: string;
  prefillWarehouseId?: string;
}

const REASONS: { value: StockAdjustmentReason; label: string }[] = [
  { value: "MANUAL", label: "Manual" },
  { value: "STOCK_TAKE", label: "Stock take" },
  { value: "OTHER", label: "Other" },
];

// Inventory → Stock Adjustment (create-only). No edit mode — an audit log
// entry is immutable; a correction is made by recording a new, opposite
// adjustment, same as an accounting ledger.
export const StockAdjustmentFormModal = ({
  open,
  onClose,
  prefillProductId,
  prefillWarehouseId,
}: StockAdjustmentFormModalProps) => {
  const { data: products = [] } = useGetProductsQuery();
  const { data: warehouses = [] } = useGetWarehousesQuery();
  const [adjustStock, { isLoading: saving }] = useAdjustStockMutation();

  const [productId, setProductId] = useState(prefillProductId ?? "");
  const [warehouseId, setWarehouseId] = useState(prefillWarehouseId ?? "");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState<StockAdjustmentReason>("MANUAL");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  // Re-apply the prefill (or lack thereof) fresh every time the popup opens —
  // this component stays mounted across opens/closes now, unlike before when
  // it was a fresh page load each time.
  useEffect(() => {
    if (!open) return;
    setProductId(prefillProductId ?? "");
    setWarehouseId(prefillWarehouseId ?? "");
    setDelta("");
    setReason("MANUAL");
    setNotes("");
    setFormError(null);
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillProductId, prefillWarehouseId]);

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!productId) errors.productId = "Select a product.";
    if (!warehouseId) errors.warehouseId = "Select a warehouse.";
    const deltaNumber = Number(delta);
    if (!delta.trim() || Number.isNaN(deltaNumber) || deltaNumber === 0) {
      errors.delta = "Enter a non-zero quantity change (positive to add stock, negative to remove).";
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      await adjustStock({
        productId,
        warehouseId,
        delta: deltaNumber,
        reason,
        notes: notes.trim() || undefined,
      }).unwrap();
      onClose();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save stock adjustment.",
      );
    }
  };

  return (
    <Modal
      open={open}
      title="New stock adjustment"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save adjustment"}
          </button>
        </>
      }
    >
      {formError ? <div className="form-error">{formError}</div> : null}

      <div className="form-grid">
        <Field label="Product *" error={fieldErrors.productId}>
          <select
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
              setFieldErrors((prev) => (prev.productId ? { ...prev, productId: undefined } : prev));
            }}
          >
            <option value="">— Select product —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.productName} ({p.productCode})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Warehouse *" error={fieldErrors.warehouseId}>
          <select
            value={warehouseId}
            onChange={(e) => {
              setWarehouseId(e.target.value);
              setFieldErrors((prev) => (prev.warehouseId ? { ...prev, warehouseId: undefined } : prev));
            }}
          >
            <option value="">— Select warehouse —</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Quantity change *" error={fieldErrors.delta}>
          <input
            type="number"
            value={delta}
            onChange={(e) => {
              setDelta(e.target.value);
              setFieldErrors((prev) => (prev.delta ? { ...prev, delta: undefined } : prev));
            }}
            placeholder="e.g. 10 or -5"
          />
        </Field>
        <label className="field">
          Reason
          <select value={reason} onChange={(e) => setReason(e.target.value as StockAdjustmentReason)}>
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field field--full">
          Notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
    </Modal>
  );
};
